/**
 * AssetDetailDrawer - Read-only per-asset detail panel for the Image Assets Library.
 *
 * Presentational only: the parent (the /assets Library page) owns `open` + `assetId`
 * and controls visibility. This drawer fetches the full detail object via
 * `useClientImageAsset` and renders preview, metadata, and provenance.
 *
 * Review actions (Approve / Approve-with-edits / Do-not-use) are NOT here — they are
 * added later (P3.2) by a different teammate. See the placeholder near the footer.
 */

import { FileText, Download } from "lucide-react"
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet"
import { ScrollArea } from "@/components/ui/scroll-area"
import { Badge } from "@/components/ui/badge"
import { Separator } from "@/components/ui/separator"
import { Skeleton } from "@/components/ui/skeleton"
import { useClientImageAsset } from "@/hooks/useClientImageAsset"
import type { ClientImageAssetDetail } from "@/services/clientImageAssetsService"
import { AssetStateChip } from "./AssetStateChip"
import { AssetTrustBadge } from "./AssetTrustBadge"

export interface AssetDetailDrawerProps {
  tenantId: string
  assetId: string | null
  open: boolean
  onOpenChange: (open: boolean) => void
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatBytes(bytes?: number | null): string | null {
  if (bytes === undefined || bytes === null || Number.isNaN(bytes)) return null
  if (bytes <= 0) return "0 B"
  const units = ["B", "KB", "MB", "GB"]
  const exponent = Math.min(
    Math.floor(Math.log(bytes) / Math.log(1024)),
    units.length - 1
  )
  const value = bytes / Math.pow(1024, exponent)
  return `${value.toFixed(value >= 10 || exponent === 0 ? 0 : 1)} ${units[exponent]}`
}

function formatDate(iso?: string | null): string | null {
  if (!iso) return null
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return iso
  return date.toLocaleString()
}

/** Pull a width x height pair off the detail object if the backend ever supplies it. */
function readDimensions(asset: ClientImageAssetDetail): string | null {
  const record = asset as unknown as Record<string, unknown>
  const width = record.width ?? record.image_width
  const height = record.height ?? record.image_height
  if (typeof width === "number" && typeof height === "number") {
    return `${width} × ${height} px`
  }
  const dimensions = record.dimensions
  if (typeof dimensions === "string" && dimensions.trim()) return dimensions
  return null
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function Field({ label, value }: { label: string; value?: string | null }) {
  if (value === undefined || value === null || value === "") return null
  return (
    <div className="space-y-0.5">
      <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </dt>
      <dd className="break-words text-sm text-foreground">{value}</dd>
    </div>
  )
}

function SectionHeading({ children }: { children: React.ReactNode }) {
  return (
    <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
      {children}
    </h3>
  )
}

function LoadingBody() {
  return (
    <div className="space-y-4">
      <Skeleton className="h-48 w-full rounded" />
      <div className="flex gap-2">
        <Skeleton className="h-5 w-20 rounded-full" />
        <Skeleton className="h-5 w-24 rounded-full" />
      </div>
      <Skeleton className="h-4 w-3/4" />
      <Skeleton className="h-4 w-1/2" />
      <Skeleton className="h-4 w-2/3" />
    </div>
  )
}

function EmptyBody() {
  return (
    <div className="flex h-48 flex-col items-center justify-center gap-2 text-center">
      <FileText className="h-8 w-8 text-muted-foreground" />
      <p className="text-sm text-muted-foreground">
        Asset details could not be loaded.
      </p>
    </div>
  )
}

function Preview({ asset }: { asset: ClientImageAssetDetail }) {
  if (asset.url) {
    return (
      <img
        className="w-full rounded object-cover"
        style={{ maxHeight: "40vh" }}
        src={asset.url}
        alt={asset.alt_text}
      />
    )
  }

  const extLabel = asset.extension
    ? `${asset.extension.toUpperCase()} source file`
    : "Source file"

  return (
    <div className="flex h-40 w-full flex-col items-center justify-center gap-2 rounded border border-dashed border-border bg-muted text-muted-foreground">
      <FileText className="h-8 w-8" />
      <span className="text-sm font-medium">{extLabel}</span>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function AssetDetailDrawer({
  tenantId,
  assetId,
  open,
  onOpenChange,
}: AssetDetailDrawerProps) {
  const { data: asset, isLoading } = useClientImageAsset(
    tenantId,
    open ? assetId : null
  )

  const dimensions = asset ? readDimensions(asset) : null
  const sizeLabel = asset ? formatBytes(asset.size_bytes) : null

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full overflow-hidden p-0 sm:max-w-md"
      >
        <div className="flex h-full flex-col">
          <SheetHeader className="space-y-1 border-b border-border px-6 py-4 text-left">
            <SheetTitle className="break-words">
              {asset?.filename ?? "Asset details"}
            </SheetTitle>
            <SheetDescription>
              Read-only asset metadata and provenance.
            </SheetDescription>
          </SheetHeader>

          <ScrollArea className="flex-1">
            <div className="space-y-6 px-6 py-5">
              {isLoading ? (
                <LoadingBody />
              ) : !asset ? (
                <EmptyBody />
              ) : (
                <>
                  {/* Preview */}
                  <div className="space-y-3">
                    <Preview asset={asset} />
                    {asset.original_url ? (
                      <a
                        href={asset.original_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 text-sm font-medium text-coral hover:text-coral-hover"
                      >
                        <Download className="h-4 w-4" />
                        Download original
                      </a>
                    ) : null}
                  </div>

                  {/* State + trust + category chips */}
                  <div className="flex flex-wrap items-center gap-2">
                    <AssetStateChip state={asset.state} />
                    <AssetTrustBadge
                      labelSource={asset.label_source}
                      reviewedAt={asset.reviewed_at}
                      labelStatus={asset.label_status}
                    />
                    {asset.category ? (
                      <Badge variant="outline">{asset.category}</Badge>
                    ) : null}
                  </div>

                  <Separator />

                  {/* Metadata */}
                  <div className="space-y-4">
                    <SectionHeading>Metadata</SectionHeading>
                    <dl className="space-y-3">
                      <Field label="Filename" value={asset.filename} />
                      <Field label="Category" value={asset.category} />
                      <Field label="Description" value={asset.description} />
                      <Field label="Alt text" value={asset.alt_text} />
                    </dl>

                    {asset.tags && asset.tags.length > 0 ? (
                      <div className="space-y-1.5">
                        <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                          Tags
                        </span>
                        <div className="flex flex-wrap gap-1.5">
                          {asset.tags.map((tag) => (
                            <Badge
                              key={tag}
                              variant="secondary"
                              className="text-xs"
                            >
                              {tag}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    ) : null}

                    <dl className="grid grid-cols-2 gap-3">
                      <Field label="Size" value={sizeLabel} />
                      <Field
                        label="Extension"
                        value={asset.extension ?? null}
                      />
                      <Field label="Dimensions" value={dimensions} />
                    </dl>
                  </div>

                  <Separator />

                  {/* Provenance */}
                  <div className="space-y-4">
                    <SectionHeading>Provenance</SectionHeading>
                    <dl className="space-y-3">
                      <Field
                        label="Source path"
                        value={asset.source_path}
                      />
                      <Field
                        label="Original path"
                        value={asset.original_path}
                      />
                      <Field
                        label="Client folder"
                        value={asset.client_folder}
                      />
                      <Field
                        label="Imported"
                        value={formatDate(asset.created_at)}
                      />
                      <Field
                        label="Label source"
                        value={asset.label_source}
                      />
                      <Field
                        label="Label run ID"
                        value={asset.label_run_id}
                      />
                      <Field
                        label="Storage upload status"
                        value={asset.storage_upload_status}
                      />
                      <Field
                        label="Reviewed at"
                        value={formatDate(asset.reviewed_at)}
                      />
                      <Field
                        label="Reviewed by"
                        value={asset.reviewed_by}
                      />
                    </dl>
                  </div>
                </>
              )}
            </div>
          </ScrollArea>

          {/* P3.2: review actions go here */}
          <div className="border-t border-border px-6 py-3" />
        </div>
      </SheetContent>
    </Sheet>
  )
}

export default AssetDetailDrawer
